import React from "react";
import ReactDOM from "react-dom";
import Select, { type StylesConfig } from "react-select";

import { EditorConfig, Extension, type MutableProseNode, type MutableProseNodeAttrs, ProseReact } from ".";
import { faInfoCircle, faTimes, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { cloneDeep } from "lodash";
import { Fragment, Node as ProseNode, Schema } from "prosemirror-model";
import { EditorState } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import uuid from "uuid";

import { nodeAttrsToStatus } from "~/actionItems/utils";
import AppContext from "~/common/AppContext";
import { zindexModal } from "~/css/zindexes";
import type { AssigneeStatusType } from "~/notes/AssignableDropdown";
import type { EditorContextParticipant } from "~/stream/NoteCard";
import { Tooltip } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import reactSelect from "~/ui/react-select";

import { cloneNode } from "../dom";
import { getAllMarks } from "./marks";
import {
	BulletPoint,
	CustomEmoji,
	HardBreak,
	Header,
	HorizontalRule,
	KeyResult,
	Mention,
	NumberedListItem,
	Tag,
	TextNode,
	Unstyled,
} from "./nodes";
import {
	Agenda,
	GlobalPlaceholder,
	Indent,
	InputRules,
	MentionProvider,
	Paste,
	Placeholder,
	SlashProvider,
	Todo,
	TypeAhead,
} from "./plugins";
import { AI_TOPICS_TEMPLATE_ID } from "./plugins/aiTopics";
import { BlockActions } from "./plugins/blockActions";
import BlockBoundary from "./plugins/blockBoundary";
import { getContext } from "./plugins/context";
import EmojiProvider, { PasteBuiltInEmoji } from "./plugins/emoji";
import { Keyboard } from "./plugins/keyboard";
import { DUMMY_USER, userName } from "./plugins/prompt";
import SelectedBlock from "./plugins/selectedBlock";
import TagProvider from "./plugins/tag";
import Toolbar, { type ToolbarProps } from "./plugins/toolbar";

import type { NoteCard_viewer$data as Viewer } from "~/__generated__/NoteCard_viewer.graphql";

import styles from "./css/snippet.module.css";

type Attrs = { [k: string]: unknown };
export type ProseJSON =
	| { type: "text"; text: string }
	| {
			type: "todo" | "agenda" | "header-one" | "mention" | "unstyled" | "bullet-point";
			attrs?: Attrs;
			content: ProseJSON[];
	  };

export type CustomSectionConfigs = {
	viewer?: Viewer;
};

export const getSnippetPreviewConfig = (currentUserId?: string | undefined) => {
	let extensions: Extension[] = [
		...getAllMarks({ disableLinkToolBar: true, linkEditable: false, forStaticDOM: true }),
		new TextNode(),
		new Indent(),
		new Unstyled(),
		new BulletPoint(),
		new NumberedListItem(),
		new HardBreak(),
		new HorizontalRule(),
		new Placeholder(),
		new Todo({ disableJira: true, localToggle: true, disabledReadOnly: true, viewerId: currentUserId }),
		new Agenda({ localToggle: true, disabledReadOnly: true, viewerId: currentUserId }),
		new Mention({ currentUserId }),
		new KeyResult(),
		new CustomEmoji(),
	];

	return new EditorConfig({
		extensions,
		nodes: {
			doc: {
				content: "block+",
			},
		},
	});
};

export type Snippet = {
	id: string;
	name: string;
	defaultSectionName: string;
	// Not using concrete ProseNode because the schema is not determined at this point
	blocksJSON: Readonly<ProseJSON[]>;
	fragmentJSON?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
	customTemplateId?: string; // used when caching element in promptPlaceholder.tsx
};

function node(type: Exclude<ProseJSON["type"], "text">, attrs: Attrs, ...content: ProseJSON[]): ProseJSON {
	return {
		type,
		attrs,
		content,
	};
}

function agenda(attrs: Attrs | null, ...content: ProseJSON[]): ProseJSON {
	return node(
		"agenda",
		{
			blockId: createUuid(),
			creator: currentUserId(),
			assignees: [{ id: currentUserId(), status: nodeAttrsToStatus(attrs) }],
			...attrs,
		},
		...content,
	);
}

function todo(attrs: Attrs | null, ...content: ProseJSON[]): ProseJSON {
	return node(
		"todo",
		{
			blockId: createUuid(),
			assignees: [{ id: currentUserId(), status: nodeAttrsToStatus(attrs) }],
			creator: currentUserId(),
			...attrs,
		},
		...content,
	);
}

const unstyled = node.bind(null, "unstyled");
const bulletPoint = node.bind(null, "bullet-point");
const mention = node.bind(null, "mention");

function createUuid(): string {
	return "{{uuid}}";
}

function currentUserName(): ProseJSON {
	return text("{{current_user_name}}");
}

function currentUserId(): string {
	return "{{current_user_id}}";
}

function text(text: string): ProseJSON {
	return {
		type: "text",
		text,
	};
}

function mentionCurrentUser(): ProseJSON {
	return mention(
		{
			id: currentUserId(),
			entityId: createUuid(),
		},
		currentUserName(),
	);
}

function listItem(indent: number, ...content: ProseJSON[]): ProseJSON {
	let factory = bulletPoint;
	if (indent === 0) factory = unstyled;
	return factory(
		{
			blockId: createUuid(),
			creator: currentUserId(),
			indent,
		},
		...content,
	);
}

function normalize(parent: ProseJSON): ProseJSON {
	if (parent.type === "text") return parent;

	const newContent: ProseJSON[] = [];
	for (const node of parent.content) {
		const last = newContent[newContent.length - 1];

		if (!last || node.type !== "text" || last.type !== "text") {
			newContent.push(normalize(node));
		} else {
			last.text += node.text;
		}
	}

	return {
		...parent,
		content: newContent,
	};
}

export const AI_TOPICS_TEMPLATE = {
	id: AI_TOPICS_TEMPLATE_ID,
	name: "[AI-Generated Agenda] AI Suggested Topics",
	defaultSectionName: "AI-Generated Agenda Section",
	blocksJSON: [agenda({ placeholder: "Add a talking point" })],
};

// The `id` should not be reused, different content should have different IDs
export const BUILT_IN_SNIPPETS: Snippet[] = [
	{
		id: "talking_point",
		name: "Talking Point",
		defaultSectionName: "Talking Points",
		blocksJSON: [agenda({ placeholder: "Add a talking point" })],
	},
	{
		id: "action_item",
		name: "Action Item",
		defaultSectionName: "Action Items",
		blocksJSON: [todo({ placeholder: "Add an action item" })],
	},
	{
		id: "roundtable",
		name: "Roundtable",
		defaultSectionName: "Roundtable",
		blocksJSON: [agenda(null, mentionCurrentUser(), text(": "))],
	},
	{
		id: "roundtable-with-bullet",
		name: "Roundtable with Bullet",
		defaultSectionName: "Roundtable",
		blocksJSON: [agenda(null, mentionCurrentUser()), listItem(1)],
	},
	{
		id: "roundtable-updates",
		name: "Roundtable Updates",
		defaultSectionName: "Updates",
		blocksJSON: [
			agenda(null, mentionCurrentUser()),
			listItem(2, text("Updates & Wins:")),
			listItem(3),
			listItem(2, text("Priorities:")),
			listItem(3),
			listItem(2, text("Blockers:")),
			listItem(3),
		],
	},
	{
		id: "roundtable-check-in",
		name: "Roundtable Check-In",
		defaultSectionName: "Check-In",
		blocksJSON: [
			agenda(null, mentionCurrentUser()),
			listItem(2, text("New since last check-in:")),
			listItem(3),
			listItem(2, text("Priorities:")),
			listItem(3),
			listItem(2, text("Blockers:")),
			listItem(3),
		],
	},
	{
		id: "daily-stand-up",
		name: "Daily Stand-Up",
		defaultSectionName: "Check-In",
		blocksJSON: [
			agenda(null, mentionCurrentUser()),
			listItem(2, text("Yesterday:")),
			listItem(3),
			listItem(2, text("Today:")),
			listItem(3),
			listItem(2, text("Blockers:")),
			listItem(3),
		],
	},
	{
		id: "roundtable-personal-and-work",
		name: "Roundtable Personal & Work",
		defaultSectionName: "Roundtable",
		blocksJSON: [
			agenda(null, mentionCurrentUser()),
			listItem(2, text("Personal:")),
			listItem(3),
			listItem(2, text("Work:")),
			listItem(3),
		],
	},
	{
		id: "mood-o-meter-smilies",
		name: "Mood-o-Meter Smilies",
		defaultSectionName: "Mood-O-Meter",
		blocksJSON: [agenda(null, mentionCurrentUser(), text(": 😔 🙁 😐 🙂 😀"))],
	},
	{
		id: "mood-o-meter-traffic-light",
		name: "Mood-o-Meter Traffic Light",
		defaultSectionName: "Mood-O-Meter",
		blocksJSON: [agenda(null, mentionCurrentUser(), text(": 🔴 🟡 🟢"))],
	},
];

export const CUSTOM_TEMPLATE_ID = "custom-template";
export const CUSTOM_TEMPLATE = {
	id: CUSTOM_TEMPLATE_ID,
	name: "Custom Template",
	defaultSectionName: "Custom Template",
	blocksJSON: [],
};

export function renderSnippet(
	blocksJSON: Snippet["blocksJSON"],
	schema: Schema,
	context: { currentUser: EditorContextParticipant },
) {
	const nodes: ProseNode[] = blocksJSON.map(i => schema.nodeFromJSON(normalize(i)));
	const iteratee = (node: MutableProseNode) => {
		// It's not safe to change the content directly in this iteratee
		// So we need to re-construct them in the end.
		if (node.isText) {
			if (node.text === "{{current_user_name}}") {
				node.text = userName(context.currentUser);
			}
		} else {
			for (let [k, v] of Object.entries(node.attrs)) {
				if (v === "{{uuid}}") {
					(node.attrs as MutableProseNodeAttrs)[k] = uuid.v4();
				} else if (v === "{{current_user_id}}") {
					(node.attrs as MutableProseNodeAttrs)[k] = context.currentUser.id;
				}
				if (k === "assignees") {
					(node.attrs as MutableProseNodeAttrs)[k] = node.attrs.assignees.map((assignee: AssigneeStatusType) => {
						return {
							id:
								assignee.id === "{{current_user_id}}" || assignee.id === "dummy" ? context.currentUser.id : assignee.id,
							status: assignee.status,
						};
					});
				}
			}
		}
	};
	for (let node of nodes) {
		iteratee(node);
		node.descendants(node => {
			iteratee(node);
		});
	}
	return nodes.map(n => schema.nodeFromJSON(n.toJSON()));
}

export function convertBuiltInToCustom(snippet: Snippet) {
	const editorConfig = getSnippetPreviewConfig();
	const nodes = renderSnippet(snippet.blocksJSON, editorConfig.schema, { currentUser: DUMMY_USER });
	const doc = editorConfig.schema.nodes.doc.createChecked({}, nodes);
	return {
		...CUSTOM_TEMPLATE,
		fragmentJSON: doc.content.toJSON(),
	};
}

export function normalizeFragmentJSON(
	fragmentJSON: Snippet["fragmentJSON"],
	context: { currentUser: EditorContextParticipant },
) {
	if (!fragmentJSON) return;
	// create deep clone since fragmentJSON is immutable
	const clonedFragment = cloneDeep(fragmentJSON);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const walker = (fragment: any) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const content = fragment.map((node: any) => {
			if (node.type === "mention" && node.attrs["id"] === "dummy") {
				node.attrs["id"] = context.currentUser.id;
				node.attrs["entityId"] = uuid.v4();
				node.content = node.content.map((n: { text: string; type: string }) => {
					if (n.type === "text") {
						if (n.text === "attendee") {
							n.text = userName(context.currentUser);
						}
					}
					return n;
				});
				return node;
			}
			// If the node has a blockId, we need to generate a new uuid for it to avoid conflict
			// when we use the snippet multiple times.
			if (node?.attrs?.blockId !== undefined) {
				node.attrs.blockId = uuid.v4();
			}
			if (node.attrs) {
				if (
					"assignees" in node.attrs &&
					!node.attrs["assignees"].some((assignee: { [key: string]: string }) => context.currentUser.id === assignee.id)
				) {
					node.attrs["assignees"] = [{ id: context.currentUser.id, status: nodeAttrsToStatus(node.attrs) }];
				}
				if ("creator" in node.attrs) {
					node.attrs["creator"] = context.currentUser.id;
				}
			}
			node.content && (node.content = walker(node.content));
			return node;
		});
		return content;
	};
	walker(clonedFragment);
	return clonedFragment;
}

type SnippetSelectionOption = {
	label: string;
	value: string;
	snippet: Snippet;
};
type SnippetSelectProps = {
	onChange: (v: Snippet) => void;
	value?: Snippet;
	initialValue?: Snippet;
	allowEmpty?: boolean;
	className?: string;
};
export function SnippetSelect({ onChange, value, initialValue, className, allowEmpty = false }: SnippetSelectProps) {
	const options = React.useMemo(() => {
		// Just the built-ins + already selected for now
		const res = BUILT_IN_SNIPPETS.map(i => ({ value: i.id, label: i.name, snippet: i }));
		res.push({ value: CUSTOM_TEMPLATE.id, label: CUSTOM_TEMPLATE.name, snippet: CUSTOM_TEMPLATE });
		if (initialValue && !res.find(r => r.value === initialValue.id)) {
			res.unshift({ value: initialValue.id, label: initialValue.name, snippet: initialValue });
		}
		return res;
	}, [initialValue]);
	const selectValue = React.useMemo(() => {
		if (!value) return;
		return options.find(o => o.value === value.id);
	}, [value, options]);
	const onSelectChange = React.useCallback(
		(v?: SnippetSelectionOption | null) => {
			v?.snippet && onChange(v.snippet);
		},
		[onChange],
	);
	// if empty is not allowed, auto choose the first value
	React.useEffect(() => {
		if (!allowEmpty && !value && options.length) {
			onChange(options[0].snippet);
		}
	}, [allowEmpty, onChange, options, value]);
	const finalClassName = cx(className, reactSelect.className);

	return (
		<Select
			{...reactSelect}
			className={finalClassName}
			options={options}
			onChange={onSelectChange}
			value={selectValue}
			menuPortalTarget={document.body}
			menuPosition="fixed"
			styles={snippetSelectStyles}
		/>
	);
}

const snippetSelectStyles: StylesConfig<{ value: string; label: string; snippet: Snippet } | undefined, false> = {
	menuPortal: provided => ({
		...provided,
		zIndex: zindexModal + 1,
	}),
	menuList: provided => ({
		...provided,
		padding: "0.5rem 0.75rem",
		margin: 0,
	}),
};

type SnippetPreviewProps = {
	snippet: Snippet;
	currentUser: EditorContextParticipant;
};

const snippetProseContext = {
	isSnippet: true,
};

export function SnippetPreview({ snippet, currentUser, onChange }: SnippetPreviewProps & { onChange?: () => void }) {
	const { user } = React.useContext(AppContext);
	const editorConfig = React.useMemo(() => {
		// Use template editor's schema for now.
		// We'll have a new editor to edit the snippets, and will use that schema
		// Also, can't call this on the module level for some reason
		return getSnippetPreviewConfig(user?.id);
	}, [user?.id]);
	const renderedDoc = React.useMemo(() => {
		const schema = editorConfig.schema;
		const nodes =
			snippet.id === CUSTOM_TEMPLATE_ID
				? Fragment.fromJSON(editorConfig.schema, normalizeFragmentJSON(snippet.fragmentJSON, { currentUser }))
				: renderSnippet(snippet.blocksJSON, editorConfig.schema, { currentUser });
		const doc = schema.nodes.doc.createChecked({}, nodes);
		return doc;
	}, [snippet, currentUser, editorConfig.schema]);
	return (
		<ProseReact
			controlledDoc={renderedDoc}
			config={editorConfig}
			editable={false}
			onChange={onChange}
			context={snippetProseContext}
			enableReactHost
		/>
	);
}

type SnippetEditorProps = {
	editorRef: (node: ProseReact) => void;
	view: EditorView;
	onChange: (state: EditorState) => void;
	snippet: Snippet;
	currentUser: EditorContextParticipant;
	customSectionConfigs?: CustomSectionConfigs;
};

const AttendeePlaceholderTooltip = ({ close }: { close: React.MouseEventHandler }) => {
	return (
		<div className={styles.mentionPlaceholder__tooltip}>
			<div className={styles.mentionPlaceholder__tooltip__icons}>
				<FontAwesomeIcon icon={faUser} />
				<IconButton icon={faTimes} onClick={close} />
			</div>
			<div className={styles.mentionPlaceholder__tooltip__text}>
				You can use @attendee as a placeholder mention. This will automatically mention the attendee that adds it to the
				note.
			</div>
		</div>
	);
};

const AttendeeMentionPlaceholderChoice = () => {
	const [showTooltip, setShowTooltip] = React.useState(false);

	const onClickInfo = React.useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setShowTooltip(true);
	}, []);

	const onClickClose = React.useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		e.nativeEvent.stopImmediatePropagation();
		setShowTooltip(false);
	}, []);

	const onClickOutside = React.useCallback(() => {
		setShowTooltip(false);
	}, []);

	return (
		<div className={styles.mentionPlaceholder}>
			<div className={`typeahead-result-item-content`}>
				<div className={`typeahead-result-item-title`}>{"Attendee"}</div>
				<div className={`typeahead-result-item-subtext`}>{"Placeholder mention"}</div>
			</div>
			<div className={styles.mentionPlaceholder__info}>
				<Tooltip
					visible={showTooltip}
					content={<AttendeePlaceholderTooltip close={onClickClose} />}
					interactive
					// this "appendTo" exists because of tippy.js positioning issue
					// see https://atomiks.github.io/tippyjs/v6/all-props/#interactive
					// removing it will render the tooltip in a wrong place in this case
					appendTo={document.body}
					onClickOutside={onClickOutside}
				>
					<IconButton icon={faInfoCircle} title={"Mention Placeholder Info"} onMouseEnter={onClickInfo} />
				</Tooltip>
			</div>
		</div>
	);
};

const MENU_EXCLUDE_CHOICES = [
	`Section (${BUILT_IN_SNIPPETS[0].defaultSectionName})`,
	`Section (${BUILT_IN_SNIPPETS[1].defaultSectionName})`,
	"Section (Custom)",
	"Embed media",
	"Record a video",
	"Attachments strip",
	"Microsoft To Do",
	"Jira",
	"ClickUp",
	"Trello",
	"monday.com",
	"Linear",
	"Asana",
];

const excludeChoiceFromMenu: ToolbarProps["excludeChoice"] = choice =>
	MENU_EXCLUDE_CHOICES.some(c => c === choice.title);

export function SnippetEditor({
	editorRef,
	view,
	onChange,
	snippet,
	currentUser,
	customSectionConfigs,
}: SnippetEditorProps) {
	const { user } = React.useContext(AppContext);
	const attendeePlaceholderChoice = React.useMemo(
		() => ({
			...currentUser,
			render: (container: HTMLElement) => {
				ReactDOM.render(<AttendeeMentionPlaceholderChoice />, container);
				return true;
			},
		}),
		[currentUser],
	);

	const toolbarMountPointElementId = "snippet-editor-toolbar-mount-point";
	const editorConfig = React.useMemo(() => {
		const extensions = [
			...getAllMarks({ linkEditable: true, placeholderEditable: true }),
			new BlockActions(),
			new Keyboard(),
			new TextNode(),
			new Indent(),
			new Unstyled(),
			new BlockBoundary(),
			new BulletPoint(),
			new NumberedListItem(),
			new HardBreak(),
			new HorizontalRule(),
			new Header({ inCustomSection: true }),
			new Mention({ currentUserId: user?.id }),
			new Tag({ showToolbar: false, clickable: false }),
			new Toolbar({
				excludeChoice: excludeChoiceFromMenu,
				compact: true,
				mountPointId: toolbarMountPointElementId,
				className: styles.snippetEditorToolbar,
			}),
			new Todo({ disableJira: true, localToggle: true, viewerId: user?.id }),
			new Agenda({ localToggle: true, viewerId: user?.id }),
			new TypeAhead({
				providers: [
					new SlashProvider({
						exclude: MENU_EXCLUDE_CHOICES,
					}),
					new TagProvider({ viewerIsGuest: customSectionConfigs?.viewer?.user.isGuest || false }),
					new MentionProvider({
						extraUsers: [attendeePlaceholderChoice],
					}),
					new EmojiProvider(),
				],
			}),
			new Paste(),
			new SelectedBlock(),
			new InputRules(),
			new GlobalPlaceholder({ text: "@attendee add your custom template content here…" }),
			new KeyResult(),
			new CustomEmoji(),
			new PasteBuiltInEmoji(),
		];
		return new EditorConfig({
			extensions,
			nodes: {
				doc: {
					content: "block+",
				},
			},
		});
	}, [attendeePlaceholderChoice, customSectionConfigs?.viewer?.user.isGuest, user?.id]);

	// calculate initial doc from snippet
	const initialDoc = React.useMemo(() => {
		if (snippet?.fragmentJSON) {
			const schema = editorConfig.schema;
			const nodes = Fragment.fromJSON(schema, snippet.fragmentJSON);
			return schema.nodes.doc.createChecked({}, nodes);
		}
		return {
			content: [],
			type: "doc",
		};
	}, [snippet, editorConfig.schema]);

	const [tags, setTags] = React.useState(getContext(view.state)?.tags);
	// the tags available inside custom section editor come from the editorView where it is opened from.
	// update the tags after new one created, otherwise the newly created one won't be able to be accessed
	// in this custom section editor immediately. Since we got the tags from context, it won't update itself
	// unless we make it. This function is used in TagModal.tsx as a callback after a tag is created.
	const updateTags = React.useCallback(() => {
		setTags(getContext(view.state)?.tags);
	}, [view]);

	return (
		<>
			<div id={toolbarMountPointElementId} className={styles.snippetEditorToolbarMountPoint}></div>
			<ProseReact
				enableReactHost
				ref={editorRef}
				config={editorConfig}
				initialDoc={initialDoc}
				onChange={onChange}
				context={{
					isCustomSection: true,
					tags,
					onTagCreated: updateTags,
					participants: getContext(view.state)?.participants,
				}}
			/>
		</>
	);
}

const RENDERED_SNIPPET_CACHE: { [key: string]: DocumentFragment } = {};

/**
 * Copy and return the DOM of a rendered snippet
 * @param props The props used to render SnippetPreview
 * @param element The element to copy the rendered dom to.
 * @returns Promise<HTMLElement> that resolves to an HTML element with rendered content
 */
export function renderSnippetToDOM(props: SnippetPreviewProps, element?: HTMLElement) {
	const cacheKey = `${props.snippet.id}|${props.currentUser.id}`;
	return new Promise<DocumentFragment>(resolve => {
		const isCustomTemplate = props.snippet.id === CUSTOM_TEMPLATE_ID;
		if (!isCustomTemplate && cacheKey in RENDERED_SNIPPET_CACHE) {
			const result = RENDERED_SNIPPET_CACHE[cacheKey];
			const clone = cloneNode(result, true);
			element && element.appendChild(clone);
			return;
		}
		const el = document.createElement("div");
		const onChange = () => {
			const result = new DocumentFragment();
			el.childNodes.forEach(n => result.appendChild(n.cloneNode(true)));
			const clone = cloneNode(result, true);
			resolve(clone);
			element && element.appendChild(clone);
			RENDERED_SNIPPET_CACHE[cacheKey] = result;
			ReactDOM.unmountComponentAtNode(el);
		};
		// eslint-disable-next-line fellow/no-inline-disable
		// eslint-disable-next-line react/jsx-no-bind
		ReactDOM.render(<SnippetPreview {...props} onChange={onChange} />, el);
	});
}
